/**
 * Created by m.igrachev on 8/9/2017.
 */
import React from "react";
import PropTypes from "prop-types";
import Paragraph from "../paragraph/Paragraph";
import ControlMessages from "../control-messages/ControlMessages";
import ControlMessageGroup from "../control-messages/ControlMessageGroup";
import Tooltip from "../tooltip/Tooltip";
import ArrayUtils from "@tinqin/tinqin-utils/src/array";
import ObjectUtils from "@tinqin/tinqin-utils/src/object";
import CommonUtils from "@tinqin/tinqin-utils/src/common";
import Focusable from "../focusable/Focusable";
import documentedProps from "./documentation/ValidatableDocumentedProps";

export default class Validatable extends Focusable {
    //IMPORTANT: Is is strictly forbidden to do "setState" in this class, since it is designed to
    //IMPORTANT: only be extended and not directly used. React will not work properly if we setState here!
    performRequiredValidation(value, validationOptions) {
        if (!this.props.required) {
            return {
                //errorMessage: undefined,
                valid: undefined,
                failedValidations: {}
            };
        }
        //if required and validation method defined
        if (this.props.validationMethod) {
            let validationResults = this.props.validationMethod(value, validationOptions);
            //Note: Required validation is always error level!
            let failedErrors = validationResults.error || {};
            let performedValidations = failedErrors.types || [];
            let errorMessages = failedErrors.messages || [];

            if (performedValidations.includes("required")) {
                return {
                    //Required validation is stronger than any other validation!
                    valid: false,
                    failedValidations: {
                        error: {
                            types: ["required"],
                            messages: [errorMessages.required]
                        }
                    }
                };
            }
        }
        return {
            valid: this.state.valid
        };
    }
    performFrontEndValidation(value, validationOptions = {}) {
        let validationResults,
            failedValidations = {};
        validationOptions.componentProps = this.props;
        validationOptions.componentState = this.state;

        //if no validation method is defined return current state
        if (this.props.validationMethod) {
            let result = {
                //errorMessage: undefined,
                valid: undefined,
                failedValidations: {}
            };
            validationResults = this.props.validationMethod(value, validationOptions);
            //if all of the validation has passed successfully
            if (ObjectUtils.isEmpty(validationResults)) {
                result.valid = true;
                return result;
            }
            //filter required validation
            failedValidations.warning = validationResults.warning || {};
            failedValidations.info = validationResults.info || {};
            let validationResultErrors = validationResults.error || {};
            let filteredTypes = ArrayUtils.arrayWithout(
                validationResultErrors.types || [],
                "required"
            );
            let filteredMessages = ObjectUtils.objectWithout(
                validationResultErrors.messages || {},
                "required"
            );
            if (filteredTypes.length) {
                failedValidations.error = {
                    types: filteredTypes,
                    messages: filteredMessages
                };
            }
            result.failedValidations = failedValidations;
            //if validation level is error, set state validity to false
            if (failedValidations.error) {
                result.valid = false;
            }

            return result;
        }
        return {
            valid: this.state.valid
        };
    }
    /*
     * Validation message component to display.
     *
     * @returns the message that shall be displayed.
     */
    retrieveValidationMessages() {
        let failedValidations = this.state.failedValidations || {};

        let currentFailures = {},
            currentMessages,
            currentLevel;
        if (failedValidations.error && !ObjectUtils.isEmpty(failedValidations.error)) {
            currentFailures = failedValidations.error;
            currentLevel = "error";
        } else if (failedValidations.warning && !ObjectUtils.isEmpty(failedValidations.warning)) {
            currentFailures = failedValidations.warning;
            currentLevel = "warning";
        } else if (failedValidations.info && !ObjectUtils.isEmpty(failedValidations.info)) {
            currentFailures = failedValidations.info;
            currentLevel = "info";
        }
        currentMessages = currentFailures.messages || {};

        let controlMessageProperties = this.props.controlMessageProperties || {};

        let paragraphMessages = [];
        Object.keys(currentMessages).forEach((messageKey, index) => {
            paragraphMessages.push(<Paragraph key={index} text={currentMessages[messageKey]} />);
        });

        if (paragraphMessages.length) {
            let errorGroupKey = currentLevel + "GroupLabel";
            let defaultGroupLabel = currentLevel.charAt(0).toUpperCase() + currentLevel.slice(1);
            let groupLabel = this.props[errorGroupKey] || defaultGroupLabel;
            let controlMessages = (
                <ControlMessages theme={controlMessageProperties.theme || "light"}>
                    <ControlMessageGroup
                        name={groupLabel}
                        skipName
                        groupType={currentLevel}
                        closable={false}
                        expandable={false}
                        skipAlertIcon={controlMessageProperties.skipAlertIcon}
                    >
                        {paragraphMessages}
                    </ControlMessageGroup>
                </ControlMessages>
            );

            let placeholderClass,
                messageIconClass = "tq-feedback-icon";
            let tooltipClass = computeTooltipClass(controlMessageProperties, currentLevel);
            if (currentLevel === "error") {
                messageIconClass += " tq-icon-cancel tq-danger-light-color";
                placeholderClass = "tq-error-input";
            } else if (currentLevel === "warning") {
                messageIconClass += " tq-icon-alert tq-warning-light-color";
            } else if (currentLevel === "info") {
                messageIconClass += " tq-icon-circle-info tq-info-light-color";
            }
            let tooltipMaxWidth;
            if (controlMessageProperties.messageMaxWidth) {
                tooltipMaxWidth = controlMessageProperties.messageMaxWidth + "px";
            }

            return (
                <div className="tq-input-feedback">
                    <Tooltip
                        extraClasses={tooltipClass}
                        value={controlMessages}
                        preferredPosition={this.props.preferredErrorTooltipDirection}
                        placeholderClass={placeholderClass}
                        maxWidth={tooltipMaxWidth}
                    >
                        <i className={messageIconClass} />
                    </Tooltip>
                </div>
            );
        }

        return "";
    }

    validationsEqual(object1, object2) {
        return CommonUtils.isEqual(object1, object2);
    }
}

Validatable.propTypes = Object.assign({}, Focusable.propTypes, {
    valid: PropTypes.bool,
    validationMethod: PropTypes.func,
    controlMessageProperties: PropTypes.object,
    required: PropTypes.bool,
    preferredErrorTooltipDirection: Tooltip.propTypes.preferredPosition,
    failedValidations: PropTypes.object,
    showValidityIcon: PropTypes.bool
});

Validatable.documentedProps = documentedProps;

Validatable.displayName = "Validatable";

function computeTooltipClass(props, level) {
    //Note: the properties:
    //alertStyle, skipAlertIcon, compactAlerts
    //that we expect here, are not documented properties (the components wont know about them)!
    //We do not indent to use these properties, but we want to be flexible enough.
    let alertLevelClass = "tq-tooltip-alert-" + level;
    let alertStyle = props.theme || "light";
    let alertStyleClass = "tq-tooltip-alert-" + alertStyle;
    let tooltipClass = "tq-tooltip-alert " + alertStyleClass + " " + alertLevelClass;
    let compactAlerts = true;
    let hasAlertIcon = !props.skipAlertIcon;
    if (typeof props.compactAlerts === "boolean") {
        compactAlerts = props.compactAlerts;
    }
    if (compactAlerts) {
        tooltipClass += " tq-tooltip-alert-compact";
        if (hasAlertIcon) {
            tooltipClass += " tq-tooltip-alert-small-icon";
        } else {
            tooltipClass += " tq-tooltip-alert-text-only";
        }
    }

    return tooltipClass;
}
